import React, { useEffect, useState } from 'react';
import './Header.scss';
import { HeaderProps } from '../../types';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { setUSDRequestScrollUtils } from '../../store/slices/utils';
import { localStorageEnum } from '../../api/enums';

const ROUTE_LABELS: Record<string, string> = {
  customers: 'Customers',
  customer: 'Customer',
  'withdrawal-manager': 'Withdrawal Manager',
  'large-withdrawals': 'Large Withdrawals',
  'usd-request': 'USD Requests',
  'usd-withheld-request': 'Held USD Transactions',
  settings: 'Settings',
  'repush-webhook': 'Repush Webhook',
  'site-configuration': 'Site Configuration',
  'payment-processor': 'Payment Processor',
  'view-all-targets': 'All Targets',
  'view-all-investments': 'All Investments',
  'piggybank-transactions': 'Piggybank Transactions',
  'flexnaira-transactions': 'Flex Naira Transactions',
  'flexdollar-transactions': 'Flex Dollar Transactions',
  'view-all-safelocks': 'All Safelocks',
  'view-all-getit': 'All GetIt',
  'view-getit': 'GetIt Details',
  'view-all-ipo': 'All IPO Investments',
  'view-ipo-offer': 'IPO Offer Details',
  'view-target': 'Target Details',
  'view-safelock': 'Safelock Details',
  'view-investment': 'Investment Details',
  'view-ipo-investment': 'IPO Investment Details',
};

const isId = (segment: string) => /^[a-f0-9-]{8,}$/.test(segment) || /^\d+$/.test(segment);

const getGreeting = (): string => {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
};

const Header = ({ name, backButton, backCallBack, backPath }: HeaderProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [firstName, setFirstName] = useState('');
  const [email, setEmail] = useState('');

  useEffect(() => {
    setFirstName(localStorage.getItem(localStorageEnum['local.storage.firstName']) ?? '');
    setEmail(localStorage.getItem(localStorageEnum['local.storage.email']) ?? '');
  }, [location.pathname]);

  const goBack = () => {
    if (location.pathname.includes('/usd-request') && location.pathname !== '/usd-request') {
      dispatch(setUSDRequestScrollUtils({ isGoBack: true }));
    }
    backCallBack && backCallBack();
    if (backPath) {
      window.location.href = backPath;
    } else window.history.back();
  };

  const buildBreadcrumbs = () => {
    const segments = location.pathname.split('/').filter(Boolean);
    const crumbs: { label: string; path: string }[] = [];
    let cumulativePath = '';

    for (const segment of segments) {
      cumulativePath += `/${segment}`;
      if (isId(segment)) continue;
      const label = ROUTE_LABELS[segment];
      if (label) {
        crumbs.push({ label, path: cumulativePath });
      }
    }
    return crumbs;
  };

  const breadcrumbs = buildBreadcrumbs();
  const avatarLetter = (firstName?.[0] ?? email?.[0] ?? '?').toUpperCase();

  return (
    <div className="tab-header">
      {/* Left: back button + page title */}
      <div className="tab-header__left">
        {backButton && (
          <button id="back-button" onClick={goBack} className="pointer" aria-label="Go back">
            <svg width="20" height="20" viewBox="0 0 24 25" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M10.5 19.5L3.5 12.5L10.5 5.5M20.5 12.5L4 12.5" stroke="#334155" strokeWidth="2" />
            </svg>
          </button>
        )}
        <div className="tab-header__title-group">
          {breadcrumbs.length > 1 && (
            <nav className="tab-header__breadcrumb" aria-label="breadcrumb">
              {breadcrumbs.map((crumb, i) => (
                <React.Fragment key={crumb.path}>
                  {i < breadcrumbs.length - 1 ? (
                    <>
                      <span className="tab-header__breadcrumb-link" onClick={() => navigate(crumb.path)}>
                        {crumb.label}
                      </span>
                      <span className="tab-header__breadcrumb-sep">/</span>
                    </>
                  ) : (
                    <span className="tab-header__breadcrumb-current">{crumb.label}</span>
                  )}
                </React.Fragment>
              ))}
            </nav>
          )}
          <p className="tab-header__page-name">{name}</p>
        </div>
      </div>

      {/* Right: greeting + avatar */}
      <div className="tab-header__right">
        {firstName && (
          <span className="tab-header__greeting">
            {getGreeting()}, <strong>{firstName}</strong> 👋
          </span>
        )}
        <div className="tab-header__avatar" title={email}>
          {avatarLetter}
        </div>
      </div>
    </div>
  );
};

export default Header;
